<template>
  <div>
    <v-card
      class="px-3 py-3"
      elevation="10"
      height="100%"
    >
      <CardTitleWithTooltip>
        <template v-slot:title>
            {{ $t('customer.settings.profile.title') }}
        </template>
        <template v-slot:tooltip>
            {{ $t('customer.settings.profile.titleTooltip') }}
        </template>
      </CardTitleWithTooltip>

      <v-card-text class="pb-2 px-1">  
        <v-container class="py-0">
          <div class="py-2 d-flex justify-center"> 
            <label class="px-2 font-weight-bold">{{ $t('customer.settings.profile.company') }}: </label>
            <label class="px-2 font-weight-regular">{{ customerProfile.companyName }} </label>
          </div>
          <div class="py-2 d-flex justify-center"> 
            <label class="px-2  font-weight-bold">{{ $t('customer.settings.profile.adminName') }}: </label>
            <label class="px-2 font-weight-regular">{{ customerProfile.adminName }} </label>
          </div>
          <div class="py-2 d-flex justify-center"> 
            <label class="px-2  font-weight-bold">{{ $t('customer.settings.profile.email') }}: </label>
            <label class="px-2 font-weight-regular">{{ customerProfile.username }} </label>
          </div>
        </v-container>
      </v-card-text>
      <v-card-actions class="pt-2 pb-2 px-4 justify-center">
        <v-btn
          @click="dialogNewPassword = true"
          v-bind="size"
          class="px-2" 
          color="primary"
          dark
        >
          {{ $t('customer.settings.profile.changePassword') }}
        </v-btn>

      </v-card-actions>
    </v-card>

    <!-- CHANGE PASSWORD MODAL START -->
    <v-dialog v-model="dialogNewPassword" persistent max-width="500px">
      <v-card class="mx-auto mt-5 pa-4">

        <CardTitleWithTooltip>
          <template v-slot:title>
              {{ $t('customer.settings.profile.changePassword') }}
          </template>
          <template v-slot:tooltip>
              {{ $t('customer.settings.profile.changePasswordTooltip') }}
          </template>
        </CardTitleWithTooltip>

        <v-card-text class="py-0 px-3">
          <v-form ref="changePasswordForm" v-model="newPasswordFormValidity" @submit.prevent="saveNewPassword">
            <v-container>
              <v-row>
                <v-col cols="12" class="px-0">
                  <v-text-field 
                    :label="$t('customer.settings.profile.password')" 
                    :type="showPassword ? 'text' : 'password'"
                    v-model="newpassword"
                    :rules="passwordRules"
                    prepend-icon="mdi-lock"
                    :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    @click:append="showPassword = !showPassword"
                    tabindex="1"
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12" class="px-0">
                  <v-text-field 
                    :label="$t('customer.settings.profile.passwordConfirmation')" 
                    :type="showConfirmPassword ? 'text' : 'password'"
                    v-model="newpasswordConfirmation"
                    :rules="newpasswordConfirmationRules.concat(newPasswordConfirmationRule)"
                    prepend-icon="mdi-lock"
                    :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    @click:append="showConfirmPassword = !showConfirmPassword"
                    tabindex="2"
                    required
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions class="px-3">
          <v-btn
            @click="closeNewPasswordDialog()"
            class="ma-1"
            color="red darken-1"
            dark
          >{{ $t('close') }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            @click="dialogNewPasswordConfirm = true"
            :disabled="!newPasswordFormValidity"
            color="green darken-1"
            dark
          >
            {{ $t('save') }}
          </v-btn>
          <ConfirmDialog
            v-if="dialogNewPasswordConfirm"
            :confirmDialogShow="dialogNewPasswordConfirm"
            @confirm-dialog-close="dialogNewPasswordConfirm = false"
            @confirm-dialog-save="saveNewPassword"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- CHANGE PASSWORD MODAL END -->

  </div>
</template>

<script>
  import { mapMutations, mapGetters } from 'vuex'
  import customerOps from '../api/users'
  import resizeMixin from '../mixins/resizeMixins'
  import i18n from '../i18n';

  export default {
    mixins: [resizeMixin],
    components: {
      CardTitleWithTooltip: require('../components/CardTitleWithTooltip').default,
      ConfirmDialog: () => import('@/components/ConfirmDialog')
    },
    created() {
      // get Customer Profile data
      this.customerProfile = { ...this.getCustomerProfile }
      // console.log(`[CustomerSettings] - created - this.customerProfile: ${JSON.stringify(this.customerProfile)}`);
    },
    data: () => ({
      customerProfile: {},
      dialogNewPassword: false,
      dialogNewPasswordConfirm: false,
      newPasswordFormValidity: false,
      newpassword: '',
      newpasswordConfirmation: '',
      newpasswordConfirmationRules: [
        value => !!value || i18n.t('validation.password.isRequired')
      ],
      passwordRules: [
        value => !!value || i18n.t('validation.password.isRequired'),
        value => {
          const pattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/;
            return (pattern.test(value) || i18n.t('validation.password.pattern'));
        }
      ],
      showPassword: false,
      showConfirmPassword: false,
    }),
    computed: {
      ...mapGetters({
        getCustomerProfile: 'users/getCustomerProfile'
      }),
      newPasswordConfirmationRule() {
        return this.newpassword === this.newpasswordConfirmation || i18n.t('validation.password.mustMatch')
      }
    },
    methods: {
      ...mapMutations({
        setSnack: 'setSnack'
      }),
      showSnack: function (snack, isError) {
        const setSnackObj = {
          message: snack,
          isError: !!isError
        }
        this.setSnack(setSnackObj)
      },
      resetPasswordForm () {
        this.$refs.changePasswordForm.reset()
      },
      resetPasswordValidation () {
        this.$refs.changePasswordForm.resetValidation()
      },
      closeNewPasswordDialog() {
        this.dialogNewPassword = false
        this.newpassword = ''
        this.newpasswordConfirmation = ''
      },
      saveNewPassword(){
        // console.log(`###[CustomerSettings] - saveNewPassword - : `, this.newpassword);
        // console.log(`###[CustomerSettings] - newpasswordConfirmation - : `, this.newpasswordConfirmation);
        // console.log(`###[CustomerSettings] - this.customerProfile - : `, this.customerProfile);

        this.dialogNewPasswordConfirm = false
        this.dialogNewPassword = false


        if (this.newpassword === this.newpasswordConfirmation) {

          const updateProfileData = {
            strict: true,
            value: {
              op: "PROFILEUPDATE",
              id: JSON.parse(sessionStorage.getItem('user')).id, // Admin collection id
              accountId: this.customerProfile.accountId,
              sessionId: this.customerProfile.sessionId,
              data: {
                username: this.customerProfile.username,
                password: this.newpassword,
                adminName: this.customerProfile.adminName,
                role: "customer"
              }
            }
          }

          // console.log(`###[CustomerSettings] - updateProfileData: `, updateProfileData);
          // console.log(`###[CustomerSettings] - updateProfileData: `, JSON.stringify(updateProfileData));

          customerOps.customerUpdateProfile(updateProfileData).then(response => {
            // console.log(`customerUpdateProfile ${status}`)

            this.resetPasswordValidation();
            this.resetPasswordForm();

            // Snackbar Success
            this.showSnack(i18n.t('customer.settings.profile.passwordUpdateSuccess'), false);
          })
          .catch(err => {
            console.error(`customerUpdateProfile error: `, err)
            // this.error = err.response.data.error

            // Snackbar error
            this.showSnack(i18n.t('customer.settings.profile.passwordUpdateError'), true);

            this.resetPasswordValidation();
            this.resetPasswordForm();
          });
        } else {
          // Dispaly alert
          this.displayAlert = true;
          this.showAlert = { 
            type: 'error', 
            message: `${i18n.t('passwordClashing')}`,
            dismissable: true
          };
          
          this.scrollToTop();
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .inputs-container {
    .col-12 {
      padding-top: 0;
      padding-bottom: 0;
    }

    .theme--light.v-input {
      padding-top: 1;
    }

    .v-input__slot {
      margin-bottom: 1;
    }
  }

  label {
    font-size: 16px;
  }
</style>